'use client';

import './globals.css';
import localFont from 'next/font/local';
import Link from 'next/link';
import Image from 'next/image';
import { useEffect } from 'react';
import { ArrowUpRight, Home, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Self-hosted from public/fonts — see src/app/layout.js for why.
const nunitoSans = localFont({
  src: '../../public/fonts/nunito-sans/nunito-sans-latin-variable.woff2',
  variable: '--font-nunito-sans',
  weight: '200 1000',
  display: 'swap',
});
const cormorant = localFont({
  src: [
    { path: '../../public/fonts/poppins/poppins-latin-400.woff2', weight: '400', style: 'normal' },
    { path: '../../public/fonts/poppins/poppins-latin-500.woff2', weight: '500', style: 'normal' },
    { path: '../../public/fonts/poppins/poppins-latin-600.woff2', weight: '600', style: 'normal' },
    { path: '../../public/fonts/poppins/poppins-latin-700.woff2', weight: '700', style: 'normal' },
  ],
  variable: '--font-cormorant',
  display: 'swap',
});

const ERROR_MAP = {
  400: { title: 'Bad Request', message: 'The server could not understand your request. Please check the URL and try again.' },
  401: { title: 'Unauthorized', message: 'You need to be signed in to view this page.' },
  403: { title: 'Access Forbidden', message: "You don't have permission to access this resource." },
  408: { title: 'Request Timeout', message: 'The server timed out waiting for the request. Please try again.' },
  429: { title: 'Too Many Requests', message: "You've made too many requests in a short period. Please wait a moment and try again." },
  500: { title: 'Critical Error', message: 'The application encountered a fatal error. Our team has been notified.' },
  502: { title: 'Bad Gateway', message: 'We received an invalid response from an upstream server.' },
  503: { title: 'Service Unavailable', message: 'The service is temporarily unavailable. Please try again shortly.' },
  504: { title: 'Gateway Timeout', message: 'The upstream server took too long to respond.' },
};

function resolveStatus(error) {
  const explicit = Number(error?.statusCode || error?.status || error?.cause?.statusCode);
  if (ERROR_MAP[explicit]) return explicit;

  const match = String(error?.message || '').match(/\b(4\d{2}|5\d{2})\b/);
  const parsed = match ? Number(match[1]) : null;
  if (parsed && ERROR_MAP[parsed]) return parsed;

  return 500;
}

export default function GlobalError({ error, reset }) {
  const code = resolveStatus(error);
  const { title, message } = ERROR_MAP[code];

  useEffect(() => {
    fetch('/api/log-error', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        message: error?.message || 'Global layout crash',
        digest:  error?.digest  || null,
        status:  code,
        url:     typeof window !== 'undefined' ? window.location.pathname : null,
      }),
    }).catch(() => {});
  }, [error, code]);

  return (
    <html lang="en" className={`${nunitoSans.variable} ${cormorant.variable} h-full antialiased`}>
      <body className="min-h-full" suppressHydrationWarning>
        <div
          className="min-h-screen flex flex-col"
          style={{
            backgroundColor: '#f8fafc',
            backgroundImage: `
              radial-gradient(circle at 20% 20%, rgba(99,102,241,0.07) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(236,72,153,0.06) 0%, transparent 50%),
              radial-gradient(circle at 50% 50%, rgba(14,165,233,0.04) 0%, transparent 50%),
              linear-gradient(rgba(99, 101, 241, 0.01) 1px, transparent 1px),
              linear-gradient(90deg, rgba(99, 101, 241, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '100% 100%, 100% 100%, 100% 100%, 40px 40px, 40px 40px',
          }}
        >
          <main className="flex-1 flex items-center justify-center px-4 pb-20 pt-20">
            <div className="max-w-lg w-full text-center relative">
              <Image
                src="/images/404-Error.svg"
                alt={`${code} Error`}
                width={320}
                height={240}
                loading='eager'
                className="mx-auto"
              />
              <p className="text-6xl font-bold text-secondary/10 leading-none select-none absolute top-2 left-50">
                {code}
              </p>
              <h1 className="text-4xl font-bold text-secondary mb-3">{title}</h1>
              <p className="text-slate-500 mb-8">{message}</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  onClick={reset}
                  className="bg-red-gradient border-0 btn-glass group h-10 px-5 cursor-pointer"
                >
                  <span className="relative z-10 flex items-center gap-2 text-white group-hover:text-secondary transition-colors duration-300">
                    <RefreshCw size={15} />
                    Try Again
                  </span>
                </Button>
                <Button asChild variant="outline" className="h-10 px-5 border-slate-200 text-secondary hover:bg-slate-50 group">
                  <Link href="/">
                    <Home size={14} className="mr-1.5" />
                    Back to Home
                    <ArrowUpRight size={14} className="ml-1.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </main>
        </div>
      </body>
    </html>
  );
}
